import {
  Show,
  type ParentProps,
  createContext,
  useContext,
  onMount,
  onCleanup,
  children as resolveChildren,
} from "solid-js";
import { Portal } from "solid-js/web";
import { X } from "lucide-solid";
import { cn } from "./utils";
import { focusablesIn, sealBackground, trapTab } from "./modalFocus";

interface DialogContextValue {
  open: () => boolean;
  onOpenChange: (open: boolean) => void;
}

const DialogContext = createContext<DialogContextValue>();

interface DialogProps extends ParentProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Dialog(props: DialogProps) {
  return (
    <DialogContext.Provider
      value={{ open: () => !!props.open, onOpenChange: (v) => props.onOpenChange?.(v) }}
    >
      {props.children}
    </DialogContext.Provider>
  );
}

interface DialogContentProps extends ParentProps {
  class?: string;
}

export function DialogContent(props: DialogContentProps) {
  const ctx = useContext(DialogContext);
  return (
    <Show when={ctx?.open()}>
      <Portal>
        <ModalPanel class={props.class} close={() => ctx?.onOpenChange(false)}>
          {props.children}
        </ModalPanel>
      </Portal>
    </Show>
  );
}

/**
 * The open dialog: modal in the way a screen reader and a keyboard need it to be. The page behind it is
 * sealed (inert) for as long as it is open, focus moves in when it opens and back out when it closes,
 * Tab stays inside, and Escape closes it — the same treatment the consent dialog gives, for the same
 * reason: a dialog a keyboard user cannot leave, or can tab out of into a page they cannot see, is a
 * trap, not a dialog.
 */
function ModalPanel(props: ParentProps<{ class?: string; close: () => void }>) {
  const c = resolveChildren(() => props.children);
  let panel: HTMLDivElement | undefined;

  onMount(() => {
    const el = panel;
    if (!el) return;
    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unseal = sealBackground(el);
    // After the children have rendered, so the first control is there to take it.
    queueMicrotask(() => (focusablesIn(el).find((f) => !f.hasAttribute("data-dialog-close")) ?? el).focus());
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        props.close();
      } else {
        trapTab(e, el);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    onCleanup(() => {
      document.removeEventListener("keydown", onKeyDown);
      unseal();
      // Only if focus is still ours to give back; the page may have moved it on in the meantime.
      if (restoreTo?.isConnected && (document.activeElement === document.body || document.activeElement === null)) restoreTo.focus();
    });
  });

  return (
    <div
      class="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={() => props.close()}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        tabindex={-1}
        onClick={(e) => e.stopPropagation()}
        class={cn(
          "relative bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto custom-scrollbar focus:outline-none",
          props.class,
        )}
      >
        <button
          onClick={() => props.close()}
          aria-label="Close"
          data-dialog-close
          class="absolute right-4 top-4 z-10 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-zinc-400 hover:text-white"
        >
          <X class="w-4 h-4" />
        </button>
        {c()}
      </div>
    </div>
  );
}

export function DialogHeader(props: ParentProps & { class?: string }) {
  return <div class={cn("flex flex-col gap-1.5 px-6 pt-6 pb-2", props.class)}>{props.children}</div>;
}

export function DialogTitle(props: ParentProps & { class?: string }) {
  return <h3 class={cn("text-lg font-semibold text-white", props.class)}>{props.children}</h3>;
}

export function DialogDescription(props: ParentProps & { class?: string }) {
  return <p class={cn("text-sm text-zinc-400", props.class)}>{props.children}</p>;
}
